import { useState, useEffect, useCallback, useRef } from "react";
import { Agent } from "../types";
import { DEFAULT_AGENTS } from "../constants";
import { getAgents } from "../../../api/dumontApi";
import type { AgentItem } from "../../../api/dumontApi";
import { useDumont } from "../../../context/DumontContext";

export interface UseAvailableAgentsResult {
  agents: Agent[];
  defaultAgentType: string | null;
  defaultAgentId: string | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function mapDumontAgentToAgent(item: AgentItem): Agent {
  const id = item.id ?? "";
  return {
    id,
    type: id || "default",
    name: item.name ?? "Agent",
    description: "Dumont agent",
    icon: "default",
    category: "General",
  };
}

/**
* Fetches available agents from Dumont API. Uses defaultAgentId from DumontContext.
*/
export const useAvailableAgents = (): UseAvailableAgentsResult => {
  const { baseUrl, token, defaultAgentId } = useDumont();
  const [agents, setAgents] = useState<Agent[]>(DEFAULT_AGENTS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchAgents = useCallback(async () => {
    if (!baseUrl || !token) {
      if (isMountedRef.current) {
        setAgents(DEFAULT_AGENTS);
        setIsLoading(false);
      }
      return;
    }
    if (isMountedRef.current) {
      setIsLoading(true);
      setError(null);
    }

    try {
      const list = await getAgents(baseUrl, token);
      if (list && list.length > 0) {
        const mapped = list.map(mapDumontAgentToAgent).filter((a) => a.id);
        if (isMountedRef.current) setAgents(mapped.length > 0 ? mapped : DEFAULT_AGENTS);
      } else {
        if (isMountedRef.current) setAgents(DEFAULT_AGENTS);
      }
    } catch (err) {
      console.error("[Dumont] Failed to fetch agents:", err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : "Failed to fetch agents");
        setAgents(DEFAULT_AGENTS);
      }
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [baseUrl, token]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  return {
    agents,
    defaultAgentType: null,
    defaultAgentId: defaultAgentId ?? null,
    isLoading,
    error,
    refetch: fetchAgents,
  };
};
